/**
 * Shared stylesheet for printable place menu / catalog documents (A4).
 *
 * Typography: system stacks only (Georgia + Helvetica Neue). No `@font-face`,
 * no remote font `<link>`, and no embedded `data:font` payloads.
 */
export const MENU_CSS_STYLE = `
@page {
    size: A4;
    margin: 14mm 13mm;
}

body {
    margin: 0;
    color: #1a221c;
    background: #fff;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: 10.6pt;
    line-height: 1.42;
}

.report.menu {
    color: #1a221c;
    background-color: #fbfaf6;
    padding: 0;
}

.report.menu * {
    box-sizing: border-box;
}

.report.menu .doc-header {
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    min-height: 34mm;
    display: flex;
    align-items: flex-end;
    color: #f7f4ec;
    isolation: isolate;
    margin-bottom: 6mm;
}

.report.menu .doc-header__media {
    position: absolute;
    inset: 0;
    z-index: -2;
    display: flex;
    pointer-events: none;
}

.report.menu .doc-header__media img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    flex: 1 1 100%;
    min-width: 0;
}

.report.menu .doc-header.--split .doc-header__photo {
    flex: 1 1 60%;
    width: 60%;
}

.report.menu .doc-header.--split .doc-header__map {
    flex: 1 1 40%;
    width: 40%;
}

.report.menu .doc-header__blend {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 48%,
        rgba(255, 255, 255, 0.9) 58%,
        rgba(255, 255, 255, 0) 68%
    );
}

.report.menu .doc-header.--plain {
    background: #2a3830;
    min-height: 22mm;
}

.report.menu .doc-header::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(180deg, rgba(20, 28, 22, 0.35) 0%, rgba(20, 28, 22, 0.82) 100%);
}

.report.menu .doc-header.--plain::after {
    background: none;
}

.report.menu .doc-header__inner {
    padding: 5mm 6mm;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
}

.report.menu .doc-header h1 {
    margin: 0;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 600;
    font-size: 26pt;
    letter-spacing: -0.01em;
}

.report.menu .doc-header p {
    margin: 0.2rem 0 0;
    font-size: 9.5pt;
    color: rgba(247, 244, 236, 0.85);
    max-width: 40ch;
}

.report.menu .doc-header__meta {
    text-align: right;
    font-size: 8.5pt;
    color: rgba(247, 244, 236, 0.85);
}

.report.menu .doc-header__meta strong {
    display: block;
    font-size: 9.5pt;
    color: #f7f4ec;
}

.report.menu .doc-subtitle {
    text-align: center;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 600;
    font-size: 13pt;
    color: #2a3830;
    margin: 0 0 1mm;
}

.report.menu .doc-note {
    text-align: center;
    color: #6b756c;
    font-size: 8.7pt;
    margin: 0 0 6mm;
}

.report.menu .legend {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4mm;
    margin-bottom: 6mm;
    padding: 3mm 4mm;
    border: 1px solid rgba(26, 34, 28, 0.16);
    border-radius: 6px;
    background: #eaeee6;
    page-break-inside: avoid;
    break-inside: avoid;
}

.report.menu .legend h2 {
    grid-column: 1 / -1;
    margin: 0 0 1mm;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 11.5pt;
    color: #2a3830;
}

.report.menu .legend dl {
    margin: 0;
    font-size: 9pt;
}

.report.menu .legend dt {
    font-weight: 700;
    color: #2a3830;
    margin: 0;
}

.report.menu .legend dt + dt {
    margin-top: 1.5mm;
}

.report.menu .legend dd {
    margin: 0 0 2mm 0;
    color: #3d4a40;
}

.report.menu .offer-print {
    border: 1px solid rgba(26, 34, 28, 0.16);
    border-radius: 6px;
    padding: 3mm 4mm;
    margin-bottom: 4mm;
    background: #fff;
    page-break-inside: avoid;
    break-inside: avoid;
}

.report.menu .offer-print__top {
    display: flex;
    align-items: flex-start;
    gap: 4mm;
}

.report.menu .offer-print__photo {
    width: 32mm;
    height: 32mm;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 4px;
    background: #eaeee6;
    flex-shrink: 0;
}

.report.menu .offer-print__body {
    flex: 1 1 auto;
    min-width: 0;
}

.report.menu .offer-print__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
}

.report.menu .offer-print h3 {
    margin: 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 12.5pt;
    color: #1a221c;
}

.report.menu .offer-print .price {
    font-weight: 700;
    color: #2a3830;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.report.menu .offer-print p.desc {
    margin: 0.6mm 0 2mm;
    color: #6b756c;
    font-size: 8.8pt;
}

.report.menu .offer-print__schedules {
    margin: 0.4mm 0 2.2mm;
    padding: 0;
    list-style: none;
    color: #6b756c;
    font-size: 8.5pt;
}

.report.menu .offer-print__schedules li {
    margin: 0.3mm 0;
}

.report.menu .offer-print__groups {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2mm 6mm;
    font-size: 8.8pt;
}

.report.menu .offer-print__groups strong {
    display: block;
    font-size: 7.8pt;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #9a7b4f;
    margin-bottom: 0.6mm;
}

.report.menu .offer-print__groups ul {
    margin: 0;
    padding-left: 1rem;
    color: #3d4a40;
}

.report.menu .doc-section {
    margin-bottom: 7mm;
}

.report.menu .doc-section__head {
    border-bottom: 1.4pt solid #3f5346;
    padding-bottom: 1.2mm;
    margin-bottom: 3mm;
    page-break-after: avoid;
    break-after: avoid;
}

.report.menu .doc-section__head h2 {
    margin: 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 15pt;
    color: #2a3830;
    text-align: left;
}

.report.menu .item {
    padding: 2.6mm 0;
    border-bottom: 0.6pt solid rgba(26, 34, 28, 0.09);
    page-break-inside: avoid;
    break-inside: avoid;
}

.report.menu .item:last-child {
    border-bottom: none;
}

.report.menu .item__top {
    display: flex;
    align-items: flex-start;
    gap: 4mm;
}

.report.menu .item__photo {
    width: 32mm;
    height: 32mm;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 4px;
    background: #eaeee6;
    flex-shrink: 0;
}

.report.menu .item__body {
    flex: 1 1 auto;
    min-width: 0;
}

.report.menu .item__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    max-width: 108mm; /* same width as option-block / price-grid */
}

.report.menu .item h3 {
    margin: 0;
    font-size: 10.8pt;
    font-weight: 650;
    color: #1a221c;
    font-family: Georgia, "Times New Roman", serif;
}

.report.menu .item__note {
    margin: 0.8mm 0 0;
    font-size: 8.8pt;
    font-style: italic;
    color: #9a7b4f;
}

.report.menu .item__desc {
    margin: 0.6mm 0 0;
    color: #6b756c;
    font-size: 8.6pt;
    max-width: 62ch;
}

.report.menu .price-note {
    margin: 1.2mm 0 0;
    font-weight: 700;
    color: #2a3830;
    font-variant-numeric: tabular-nums;
    font-size: 9.6pt;
}

.report.menu .price-list {
    margin: 1.2mm 0 0;
    padding: 0;
    list-style: none;
    font-size: 8.8pt;
    max-width: 108mm; /* same width as option-block / price-grid */
}

.report.menu .price-list li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.4mm 0;
    border-bottom: 0.4pt solid rgba(26, 34, 28, 0.09);
}

.report.menu .price-list li:last-child {
    border-bottom: none;
}

.report.menu .price-list .label {
    color: #3d4a40;
}

.report.menu .price-list .value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #1a221c;
    white-space: nowrap;
}

.report.menu table.price-grid {
    margin: 1.6mm 0 0;
    border-collapse: collapse;
    width: 100%;
    max-width: 108mm;
    font-size: 8.7pt;
}

.report.menu table.price-grid th,
.report.menu table.price-grid td {
    border: 0.6pt solid rgba(26, 34, 28, 0.16);
    padding: 1mm 2mm;
    text-align: center;
}

.report.menu table.price-grid thead th {
    background: #eaeee6;
    color: #2a3830;
    font-weight: 700;
    font-size: 7.8pt;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.report.menu table.price-grid th.zone-head {
    text-align: left;
    background: #fff;
}

.report.menu table.price-grid td.zone-label {
    text-align: left;
    font-weight: 650;
    color: #3d4a40;
    background: #eaeee6;
    white-space: nowrap;
}

.report.menu table.price-grid td.price-cell {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #1a221c;
}

.report.menu table.price-grid.compact {
    max-width: 92mm;
    font-size: 8.2pt;
}

.report.menu table.price-grid.compact th,
.report.menu table.price-grid.compact td {
    padding: 0.7mm 1.6mm;
}

.report.menu .option-block {
    margin: 2mm 0 0;
    padding: 1.6mm 0 1.6mm 3mm;
    border-left: 1.4pt solid rgba(26, 34, 28, 0.16);
    max-width: 108mm; /* same width as table.price-grid */
}

.report.menu .option-row {
    margin-bottom: 1.8mm;
}

.report.menu .option-row:last-child {
    margin-bottom: 0;
}

.report.menu .option-row__head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    width: 100%;
}

.report.menu .option-row__label {
    font-size: 9pt;
    color: #3d4a40;
    font-weight: 600;
    flex: 1 1 auto;
    min-width: 0;
}

.report.menu .option-row__head .price-note {
    margin: 0;
    flex-shrink: 0;
    white-space: nowrap;
}

.report.menu .item__body .option-block table.price-grid.compact {
    max-width: 100%;
}

.report.menu .item__body .option-block .price-list {
    max-width: 100%;
}

.report.menu .doc-footer {
    margin-top: 8mm;
    padding-top: 3mm;
    border-top: 0.6pt solid rgba(26, 34, 28, 0.16);
    font-size: 8.2pt;
    color: #6b756c;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1mm;
    page-break-inside: avoid;
    break-inside: avoid;
}
`.trim();

/**
 * Returns CSS for printable place menu documents.
 */
export const menuStyle = (): string => {
    return MENU_CSS_STYLE;
};
